/* eslint-disable jsx-a11y/media-has-caption */
/* istanbul ignore file */
/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Cropper } from '@bingads-webui-react/cropper';
import { VideoControls } from '@bingads-webui-react/video-controls';
import { ErrorBlock } from '@bingads-webui-campaign-react/image-experience';

import { ImageExperienceVideo } from '../../model/image-experience-video';
import { CONTAINER_HEIGHT } from '../constants';
import C from '../../constants';

export const useCropperEditor = (props) => {
  const {
    i18n,
    video,
    errors,
  } = props;

  const [cropSetting, setCropSetting] = React.useState({});

  const component = (
    <CropperEditor
      i18n={i18n}
      video={video}
      errors={errors}
      onCropSettingChanged={setCropSetting}
      cropSetting={cropSetting}
    />);

  return { component, getData: () => cropSetting };
};

function GetResizedVideoSize(video, containerSize) {
  const videoRatio = video.Width / video.Height;
  const containerRatio = containerSize.Width / containerSize.Height;
  if (videoRatio >= containerRatio) {
    return {
      Width: containerSize.Width,
      Height: containerSize.Width / videoRatio,
    };
  }
  return {
    Width: containerSize.Height * videoRatio,
    Height: containerSize.Height,
  };
}

const CropperEditor = (props) => {
  const {
    i18n,
    video,
    errors,
    onCropSettingChanged: _onCropSettingChanged,
  } = props;

  const option = C.videoAspectRatios[video.Type];
  const resizeRatio = video.Height / CONTAINER_HEIGHT;

  const onCropSettingChanged = _.debounce(({
    x, y, height,
  }) => {
    const newHeight = Math.floor(height * resizeRatio);
    let newWidth = Math.floor(newHeight * option.ratio);
    /* istanbul ignore if */
    if (option.ratio < 1) {
      newWidth = Math.ceil(newHeight * option.ratio);
    }
    const newX = Math.floor(x * resizeRatio);
    const newY = Math.floor(y * resizeRatio);
    /* istanbul ignore next */
    const newName = `Crop-[${newWidth}x${newHeight}]@(${newX},${newY})${video.Text ? `-${video.Text}` : ''}`;
    _onCropSettingChanged({
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
      name: newName,
    });
  }, 16);

  const videoSize = React.useMemo(
    () => GetResizedVideoSize(
      video,
      { Height: CONTAINER_HEIGHT, Width: CONTAINER_HEIGHT * option.ratio }
    ),
    [video, option.ratio]
  );

  return (
    <React.Fragment>
      <div className="description">
        {i18n.getString('ImageExperience_CropperDescription')}
      </div>
      <div className="cropper-editor crop-container">
        <VideoControls width={option.ratio * CONTAINER_HEIGHT}>
          <div
            style={
              {
                height: CONTAINER_HEIGHT,
                width: CONTAINER_HEIGHT * option.ratio,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'black',
              }
            }
          >
            <Cropper
              options={{
                ...option.cropSetting,
                targetSetting: {
                  height: videoSize.Height,
                  width: videoSize.Width,
                },
                aspectRatio: option.ratio,
                onCropEnd: onCropSettingChanged,
                onCropMove: onCropSettingChanged,
                onInitialize: cropper => onCropSettingChanged(cropper.getValue()),
              }}
            >
              <video
                src={video.Src}
                poster={video.thumbnailUrl}
                width={videoSize.Width}
                height={videoSize.Height}
                controls={false}
              />
            </Cropper>
          </div>
        </VideoControls>
      </div>
      <ErrorBlock errors={errors} />
    </React.Fragment>);
};

CropperEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  video: PropTypes.instanceOf(ImageExperienceVideo).isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  onCropSettingChanged: PropTypes.func.isRequired,
  cropSetting: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
};

CropperEditor.defaultProps = {
  errors: [],
};
